/**
 * Local stand-in for `@ctrl/tinycolor`, which our Artifactory blocks.
 *
 * antd 4 only uses `TinyColor` in `config-provider/cssVariables`, so this
 * shim keeps that subset of the API and builds it on `@ant-design/fast-color`
 * (the colour class antd itself moved to). Everything else is inherited.
 *
 * The overrides below keep TinyColor 3.x behaviour where FastColor differs:
 * - `toHsl`, `lighten` and `darken` use real HSL saturation and the same
 *   HSL to RGB maths as TinyColor. FastColor 2.x uses the HSV saturation,
 *   which washes out lightened and darkened colours.
 * - `mix` (and so `tint` and `shade`) keeps the exact alpha.
 * - `setAlpha` changes the colour in place and returns it.
 * - `toRgbString` prints `rgb(r, g, b)` with spaces and a rounded alpha.
 */

const { FastColor } = require('@ant-design/fast-color')

function clamp01(value) {
  return Math.min(1, Math.max(0, value))
}

function boundAlpha(value) {
  const alpha = Number.parseFloat(value)
  return Number.isNaN(alpha) || alpha < 0 || alpha > 1 ? 1 : alpha
}

function rgbToHsl(red, green, blue) {
  const r = red / 255
  const g = green / 255
  const b = blue / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) {
    return { h: 0, s: 0, l }
  }
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h
  if (max === r) {
    h = (g - b) / d + (g < b ? 6 : 0)
  } else if (max === g) {
    h = (b - r) / d + 2
  } else {
    h = (r - g) / d + 4
  }
  return { h: (h / 6) * 360, s, l }
}

function hueToChannel(p, q, t) {
  const hue = t < 0 ? t + 1 : t > 1 ? t - 1 : t
  if (hue < 1 / 6) {
    return p + (q - p) * 6 * hue
  }
  if (hue < 1 / 2) {
    return q
  }
  if (hue < 2 / 3) {
    return p + (q - p) * (2 / 3 - hue) * 6
  }
  return p
}

// TinyColor 3.x reads s and l back as percentages and keeps two decimals.
function truncatePercent(value) {
  return Math.trunc(value * 100 * 100) / 100 / 100
}

function hslToRgb({ h, s: saturation, l: lightness }) {
  const s = truncatePercent(saturation)
  const l = truncatePercent(lightness)
  if (s === 0) {
    return { r: l * 255, g: l * 255, b: l * 255 }
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const hue = h / 360
  return {
    r: hueToChannel(p, q, hue + 1 / 3) * 255,
    g: hueToChannel(p, q, hue) * 255,
    b: hueToChannel(p, q, hue - 1 / 3) * 255
  }
}

class TinyColor extends FastColor {
  getAlpha() {
    return this.a
  }

  setAlpha(value) {
    this.a = boundAlpha(value)
    return this
  }

  toHsl() {
    return { ...rgbToHsl(this.r, this.g, this.b), a: this.a }
  }

  lighten(amount = 10) {
    return this._withLightness(amount / 100)
  }

  darken(amount = 10) {
    return this._withLightness(-amount / 100)
  }

  mix(input, amount = 50) {
    const other = this._c(input)
    const p = amount / 100
    const blend = key => (other[key] - this[key]) * p + this[key]
    return this._c({
      r: blend('r'),
      g: blend('g'),
      b: blend('b'),
      a: blend('a')
    })
  }

  toRgbString() {
    const r = Math.round(this.r)
    const g = Math.round(this.g)
    const b = Math.round(this.b)
    return this.a === 1
      ? `rgb(${r}, ${g}, ${b})`
      : `rgba(${r}, ${g}, ${b}, ${Math.round(this.a * 100) / 100})`
  }

  _withLightness(delta) {
    const hsl = this.toHsl()
    const rgb = hslToRgb({ ...hsl, l: clamp01(hsl.l + delta) })
    return this._c({ ...rgb, a: this.a })
  }
}

module.exports = { TinyColor }
